import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Chip, IconButton, Paper, Skeleton, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { AnnotationCard } from './AnnotationCard';
import { NewAnnotationCard } from './NewAnnotationCard';
import { useAnnotationConcepts } from './useAnnotationConcepts';
import { useConceptCard } from './useConceptCard';
import type { Annotation, Concept } from '../../../workspace-common/types/responseTypes';

const Annotations = ({ conceptId }: { conceptId: string }) => {
  const theme = useTheme();
  const {
    savedAnnotationsCount,
    newAnnotationCardIds,
    handleCreateAnnotation,
    shouldShowCreateButton,
    annotations,
    annotationsLoading,
    removeNewAnnotationCardId,
  } = useAnnotationConcepts({ conceptId });

  const { formatMessage } = useIntl();

  if (annotationsLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <Skeleton variant="text" width="100%" height="100px" />
      </Box>
    );
  }
  return (
    <Box>
      {annotations && annotations.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          {annotations.map((annotation, index) => (
            <Box key={annotation.annotationId}>
              <AnnotationCard
                key={annotation.annotationId}
                annotationNumber={index + 1}
                annotation={annotation}
                conceptId={conceptId}
              />
            </Box>
          ))}
          {newAnnotationCardIds.map((tempId, index) => (
            <NewAnnotationCard
              key={tempId}
              annotationNumber={savedAnnotationsCount + index + 1}
              conceptId={conceptId}
              cardId={tempId}
              removeNewAnnotationCardId={removeNewAnnotationCardId}
            />
          ))}
          {shouldShowCreateButton && (
            <Button
              id="add-annotation-button"
              startIcon={<AddIcon />}
              displayLabel={formatMessage({
                id: 'annotationContent.annotationConfig.conceptCard.createAnnotationButton',
              })}
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: '100%',
                fontSize: '14px',
                color: theme.palette.primary.main,
              }}
              onClickHandler={handleCreateAnnotation}
            />
          )}
        </Box>
      ) : (
        <>
          {newAnnotationCardIds.length === 0 ? (
            <>
              <NewAnnotationCard
                annotationNumber={1}
                conceptId={conceptId}
                removeNewAnnotationCardId={removeNewAnnotationCardId}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
              {newAnnotationCardIds.map((tempId, index) => (
                <NewAnnotationCard
                  key={tempId}
                  annotationNumber={index + 1}
                  conceptId={conceptId}
                  cardId={tempId}
                  removeNewAnnotationCardId={removeNewAnnotationCardId}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

interface ConceptCardProps {
  concept: Concept;
  annotationCount: number;
}

const ConceptCard = ({ concept, annotationCount }: ConceptCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { getAnnotationCountSaved, isExpanded, handleSetIsExpanded } = useConceptCard({
    concept,
    annotationCount,
  });

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 0,
        paddingX: theme.spacing(4),
        paddingY: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body1">{concept.conceptName}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          {getAnnotationCountSaved > 0 && (
            <Chip
              variant="filled"
              sx={{
                backgroundColor: theme.palette.grey[200],
                size: 'small',
                fontSize: '13px',
              }}
              label={formatMessage(
                { id: 'annotationContent.annotationConfig.conceptCard.saved' },
                { annotationCount: getAnnotationCountSaved }
              )}
            />
          )}

          {isExpanded ? (
            <IconButton onClick={() => handleSetIsExpanded(false)}>
              <KeyboardArrowUp />
            </IconButton>
          ) : (
            <IconButton onClick={() => handleSetIsExpanded(true)}>
              <KeyboardArrowDown />
            </IconButton>
          )}
        </Box>
      </Box>

      {isExpanded && (
        <Box>
          <Annotations conceptId={concept.conceptId} />
        </Box>
      )}
    </Paper>
  );
};

interface AnnotationConceptsProps {
  concepts: Concept[];
  annotations: Annotation[];
}

export const AnnotationConcepts = ({ concepts, annotations }: AnnotationConceptsProps) => {
  // Memoize annotation counts per concept to avoid recalculating on every render
  const annotationCountsByConceptId = useMemo(() => {
    const counts = new Map<string, number>();
    concepts.forEach((concept) => {
      const count = annotations.filter(
        (annotation) => annotation.conceptId === concept.conceptId
      ).length;
      counts.set(concept.conceptId, count);
    });
    return counts;
  }, [concepts, annotations]);

  const theme = useTheme();
  return (
    <Box
      id="annotation-config-concept-groups-container"
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        overflow: 'auto',
        pb: theme.spacing(2),
      }}
    >
      {concepts &&
        concepts.map((concept) => (
          <ConceptCard
            key={concept.conceptId}
            concept={concept}
            annotationCount={annotationCountsByConceptId.get(concept.conceptId) ?? 0}
          />
        ))}
    </Box>
  );
};
